import { RuleMode } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import type { TriggerUserPreferences } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/users';
import { Location, type UUID } from '@cobliteam/common-types';
import { alertHookConfig, isTriggerAlertHookType, resolveConditionId, type TriggerAlertHookType } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import type { CurrentAlertHook } from '@cobliteam/hooks/src/useAlertHooks/types';
import useAlertHooksStore from '@cobliteam/hooks/src/useAlertHooks/useAlertHooksStore';
import { useCreateTrigger, useGetRules } from '@cobliteam/hooks/src/useEntity/useAlerts';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { useTranslation } from '@cobliteam/i18n';
import queryString from 'query-string';
import { useRef } from 'react';
import AlertHookConfirmLayout from './AlertHookConfirmLayout';
import { trackActivated, trackEditClicked } from './analytics';
import DialogAlertHookConfirmBlueprint from './DialogAlertHookConfirmBlueprint';

interface DialogAlertHookConfirmProps {
  current: CurrentAlertHook;
}

/**
 * Step 2 — read-only preview of the pre-configured trigger. "Ativar alerta" creates it and closes
 * (the success toast is emitted by `useCreateTrigger`'s mutation meta — the global mutation cache
 * picks it up, so this component intentionally doesn't toast); "Editar antes de ativar" opens the
 * trigger create page pre-filled via query params instead of creating anything — only variable
 * content (entity ids) travels in the URL, everything preset by the template is re-derived from
 * `alertHookConfig` on the other side.
 */
const TriggerAlertHookConfirm = ({ current, type }: DialogAlertHookConfirmProps & { type: TriggerAlertHookType }) => {
  const { t } = useTranslation(['alertHooks']);
  const close = useAlertHooksStore((state) => state.close);
  const { user } = useUser();
  const { data: rules } = useGetRules(RuleMode.FLEET_POLICY);
  const { mutateAsync: createTrigger, isPending: isCreating } = useCreateTrigger();

  // Synchronous re-entry guard: `isCreating` from useMutation only flips on the next render, so back-to-back
  // clicks before React commits would each fire a create request. The ref blocks that without an extra render.
  const isHandlingActivateRef = useRef(false);

  const config = alertHookConfig[type];
  const { copy } = config;
  const summaryRows = config.buildSummaryRows(current.params);
  const dismissKey = config.getDismissKey(current.params);

  const handleActivate = async () => {
    if (isHandlingActivateRef.current) return;

    const conditionId = resolveConditionId(rules, type, config.conditionCategoryDefault);
    if (!conditionId) return; // rule/condition unavailable — same gate as useTriggerAlertHook; button stays inert.

    const defaultChannels: TriggerUserPreferences[] = user
      ? [{ id: user.id, name: (user as { name?: string }).name ?? '', sendNotificationWebPainel: true, sendNotificationAppGestor: true, sendNotificationEmail: true }]
      : [];

    const payload = config.buildPayload(current.params, {
      name: t(copy.defaultNameKey),
      conditionCategory: config.conditionCategoryDefault,
      allowNotifications: true,
      notifiedUsersChannels: defaultChannels,
    });

    isHandlingActivateRef.current = true;
    try {
      await createTrigger({ ...payload, conditionId });
      trackActivated({ hookType: current.type, dismissKey });
      close();
    } catch {
      // Toast handled by the mutation's `meta.errorMessage`. Keep the modal open so the user can retry.
    } finally {
      isHandlingActivateRef.current = false;
    }
  };

  const handleEdit = () => {
    trackEditClicked({ hookType: current.type, dismissKey });
    // Pre-fill the create page through the template contract: rule/condition identify the template
    // (recurrence, name and the remaining defaults are derived there from `alertHookConfig`), and
    // only the journey's entity ids are forwarded. `geofenceId` is singular because that's the
    // param `TriggerGeofencesSelect` already prefills from (hooks always carry one geofence).
    const { vehicleIds, vehicleGroupIds, geofenceIds } = current.params as Partial<Record<'vehicleIds' | 'vehicleGroupIds' | 'geofenceIds', UUID[]>>;
    const target = `${Location.ALERTS_TRIGGERS_CREATE}?${queryString.stringify({
      templateRule: config.ruleCategory,
      templateCondition: config.conditionCategoryDefault,
      vehicleIds,
      vehicleGroupIds,
      geofenceId: geofenceIds?.[0],
    })}`;
    window.open(target, '_blank', 'noopener,noreferrer');
    close();
  };

  return (
    <AlertHookConfirmLayout
      iconName={copy.confirm.iconName ?? copy.suggestion.iconName}
      iconBg={copy.suggestion.iconBg}
      title={t(copy.confirm.titleKey)}
      description={t(copy.confirm.descriptionKey ?? 'alertHooks:common.confirmDescription')}
      rows={summaryRows}
      editLabel={t(copy.confirm.editCtaKey ?? 'alertHooks:common.ctaEditBeforeActivate')}
      onEdit={handleEdit}
      onActivate={handleActivate}
      isActivating={isCreating}
      onClose={close}
    />
  );
};

/** Step 2 dispatcher: trigger hooks are created here, blueprint hooks by the blueprint-api template. */
const DialogAlertHookConfirm = ({ current }: DialogAlertHookConfirmProps) =>
  isTriggerAlertHookType(current.type) ? <TriggerAlertHookConfirm current={current} type={current.type} /> : <DialogAlertHookConfirmBlueprint current={current} />;

export default DialogAlertHookConfirm;
